import { MissionAssessmentChatObjectKind, MissionAssessmentFieldName } from '@@types/api.types';
import { useChat } from '@ai-sdk/react';
import { FullAssessmentSectionId, FullAssessmentSectionStatus } from '@azymmetric/shared/types/full-assessment.types';
import type { LlmUIMessage as LlmChatMessage } from '@azymmetric/shared/types/llm.types';
import { ConversationEmptyState } from '@components/ai-elements/conversation';
import { Modal } from '@components/core/modal.component';
import { ConfirmationModal } from '@components/shared/confirm-delete-modal.component';
import { LlmChatInput } from '@components/shared/llm-chat/llm-chat-input.component';
import { LlmChatMessages } from '@components/shared/llm-chat/llm-chat-messages.component';
import type { LlmChatComment } from '@components/shared/llm-chat/llm-chat.util';
import { Button } from '@components/ui/button';
import { withForm } from '@components/ui/form/form';
import { useBuildHeaders } from '@hooks/api.hook';
import { errorToast, successToast } from '@hooks/toast.hook';
import { generateUUID } from '@lib/utils';
import { llmModelStore } from '@stores/llm-model.store';
import { useStore as useFormStore } from '@tanstack/react-form';
import { type ProvenanceAcceptEntry, provenanceItemKeys } from '@utils/mission-assessment-provenance.util';
import { DefaultChatTransport } from 'ai';
import { GripVertical, SparklesIcon } from 'lucide-react';
import React from 'react';
import { Group, Panel, Separator, useDefaultLayout } from 'react-resizable-panels';
import { useStore } from 'zustand';
import { MISSION_ASSESSMENT_AI_ENDPOINTS } from '../../../../../constants/mission-assessment-endpoints.constants';
import { missionAssessmentDraftFormOptions } from '../mission-assessment-form-options';
import { useFullAssessmentResolutionLog } from './full-assessment-resolution-log.hook';
import { FullAssessmentSectionsPanel } from './full-assessment-sections-panel.component';
import {
  FULL_ASSESSMENT_SECTION_LABELS,
  type FullAssessmentSectionPatch,
  GENERATE_FULL_ASSESSMENT_MESSAGE,
  buildFormSnapshot,
  deriveSectionStates,
  isFullAssessmentGenerationBusy,
  isSectionAcceptable,
  isUnresolvedDraft,
  orderedSectionIds,
  parseSectionPatch,
} from './full-assessment.util';

type FullAssessmentComment = LlmChatComment & {
  sectionId: FullAssessmentSectionId;
};

type FullAssessmentModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onProvenanceAccept?: (entries: ProvenanceAcceptEntry[]) => void;
  technologyId: string;
};

const fullAssessmentModalProps: FullAssessmentModalProps = {
  isOpen: false,
  onClose: () => undefined,
  onProvenanceAccept: undefined,
  technologyId: '',
};

/**
 * Full Mission Assessment Generation workspace (AZ-393): one global
 * conversation on the left, the complete assessment divided into Assessment
 * Section cards on the right. The one-click CTA fires the deterministic
 * two-wave fan-out; per-section Retry and suggest-edit comments dispatch
 * deterministically via targetSectionIds; untagged follow-ups route
 * through the orchestrator. Accept writes form state only — Save as Draft /
 * Submit Assessment remain the sole persistence paths.
 * See docs/iterations/AZ-393/AZ-393.DECISIONS.md (D7, D10).
 */
export const FullAssessmentModal = withForm({
  ...missionAssessmentDraftFormOptions,
  props: fullAssessmentModalProps,
  render: function Render(renderProps) {
    const { form, isOpen, onClose, onProvenanceAccept, technologyId } = renderProps;

    // Record field-level AI provenance (ADR-0007) for an accepted section. Scalar
    // prose / hurdle fields key by field name; collections expand to per-item keys
    // (which items are AI-assisted). Inters / Tasks have no stable item id and stay
    // section-level. See AZ-449.provenance-logic for the full map.
    const recordPatchProvenance = (patch: FullAssessmentSectionPatch, llmLogId?: string) => {
      if (!onProvenanceAccept) return;
      const entries: ProvenanceAcceptEntry[] = [];

      switch (patch.kind) {
        case 'expertNote':
          entries.push({ key: patch.identifier, llmLogId });
          break;
        case 'generalNotes':
          entries.push({ key: MissionAssessmentFieldName.GENERAL_NOTES, llmLogId });
          break;
        case 'hurdles':
          if (patch.value.nextMrl) entries.push({ key: MissionAssessmentFieldName.NEXT_MRL, llmLogId });
          if (patch.value.nextTrl) entries.push({ key: MissionAssessmentFieldName.NEXT_TRL, llmLogId });
          if (patch.value.supplyChain) entries.push({ key: MissionAssessmentFieldName.SUPPLY_CHAIN, llmLogId });
          break;
        case 'effects':
          for (const effect of patch.value) {
            if (effect.effectId && effect.useId && effect.domainId) {
              entries.push({
                key: provenanceItemKeys.effect(effect.effectId, effect.useId, effect.domainId),
                llmLogId,
              });
            }
          }
          break;
        case 'capabilities':
          for (const id of patch.value) entries.push({ key: provenanceItemKeys.capability(id), llmLogId });
          break;
        case 'communities':
          for (const id of patch.value) entries.push({ key: provenanceItemKeys.community(id), llmLogId });
          break;
        case 'killChains':
          for (const id of patch.value) entries.push({ key: provenanceItemKeys.killChain(id), llmLogId });
          break;
        case 'services':
          for (const service of patch.value) {
            if (service.serviceId) entries.push({ key: provenanceItemKeys.service(service.serviceId), llmLogId });
          }
          break;
        case 'techUses':
          for (const techUse of patch.value) {
            if (techUse.useId) entries.push({ key: provenanceItemKeys.technologyUse(techUse.useId), llmLogId });
          }
          break;
        case 'inters':
          entries.push({ key: patch.type, llmLogId });
          break;
        case 'tasks':
          entries.push({ key: FullAssessmentSectionId.TASK_RECOMMENDATIONS, llmLogId });
          break;
        case 'phases':
          entries.push({ key: FullAssessmentSectionId.PHASES, llmLogId });
          break;
      }

      if (entries.length > 0) onProvenanceAccept(entries);
    };

    const { buildHeaders } = useBuildHeaders();
    const { selectedModel } = useStore(llmModelStore);

    // useChat keeps the transport from the first render; request-body inputs are
    // read through refs at request time so model switches, live form values, and
    // one-shot section targets are never frozen at mount (same pattern as
    // LlmChatModal).
    const pendingTargetsRef = React.useRef<FullAssessmentSectionId[] | undefined>(undefined);
    const pendingCommentsRef = React.useRef<FullAssessmentComment[]>([]);
    const requestContextRef = React.useRef({ selectedModel, technologyId });
    const requestInFlightRef = React.useRef(false);
    requestContextRef.current = { selectedModel, technologyId };

    const chatIdRef = React.useRef(generateUUID());
    const chatInputRef = React.useRef<HTMLTextAreaElement>(null);
    const [input, setInput] = React.useState('');
    const [acceptedLogIds, setAcceptedLogIds] = React.useState<Set<string>>(new Set());
    const [isConfirmCloseOpen, setIsConfirmCloseOpen] = React.useState(false);
    const [pendingComments, setPendingComments] = React.useState<FullAssessmentComment[]>([]);
    const [queuedRetries, setQueuedRetries] = React.useState<FullAssessmentSectionId[]>([]);
    const [rejectedLogIds, setRejectedLogIds] = React.useState<Set<string>>(new Set());
    const [isRequestInFlight, setIsRequestInFlight] = React.useState(false);
    const wasOpenRef = React.useRef(false);
    pendingCommentsRef.current = pendingComments;

    // Live form values: seed content always mirrors what the form holds right
    // now (accepts update it; a rejected draft falls back to the real value).
    const formValues = useFormStore(form.store, (state) => state.values);

    const panelLayout = useDefaultLayout({
      id: 'full-assessment-modal-layout',
      panelIds: ['chat', 'sections'],
    });

    const { messages, sendMessage, setMessages, status, stop } = useChat<LlmChatMessage>({
      id: chatIdRef.current,
      generateId: generateUUID,
      transport: new DefaultChatTransport({
        api: MISSION_ASSESSMENT_AI_ENDPOINTS.chat,
        credentials: 'include',
        headers: async () => await buildHeaders(),
        prepareSendMessagesRequest: ({ messages: outgoingMessages }) => {
          const { selectedModel: model, technologyId: currentTechnologyId } = requestContextRef.current;

          // Explicit targets (Retry) are one-shot; staged comment sections tag
          // the message they ride with.
          // See docs/iterations/AZ-393/AZ-393.DECISIONS.md (D10).
          const retryTargets = pendingTargetsRef.current;
          pendingTargetsRef.current = undefined;
          const commentTargets = Array.from(new Set(pendingCommentsRef.current.map((comment) => comment.sectionId)));
          const targetSectionIds = retryTargets ?? (commentTargets.length > 0 ? commentTargets : undefined);

          return {
            body: {
              formSnapshot: buildFormSnapshot(form.state.values),
              messages: outgoingMessages,
              objectKind: MissionAssessmentChatObjectKind.FULL_ASSESSMENT,
              technologyId: currentTechnologyId,
              ...(model && { model }),
              ...(targetSectionIds && { targetSectionIds }),
            },
          };
        },
      }),
      onError: () => {
        errorToast({ description: 'Failed to generate the assessment' });
      },
    });

    const sectionStates = React.useMemo(() => deriveSectionStates(messages, status), [messages, status]);
    const isBusy = isFullAssessmentGenerationBusy({ isRequestInFlight, sectionStates, status });
    const {
      rejectUnaccepted,
      reset: resetResolutionLog,
      resolveLog,
    } = useFullAssessmentResolutionLog(sectionStates.results);

    const dispatchMessage = React.useCallback(
      (message: Parameters<typeof sendMessage>[0]) => {
        requestInFlightRef.current = true;
        setIsRequestInFlight(true);

        const request = sendMessage(message);
        void Promise.resolve(request).catch(() => {
          requestInFlightRef.current = false;
          setIsRequestInFlight(false);
        });
      },
      [sendMessage],
    );

    React.useEffect(() => {
      if (status === 'submitted' || status === 'streaming') {
        requestInFlightRef.current = true;
        setIsRequestInFlight(true);
        return;
      }

      if (status === 'error' || status === 'ready') {
        requestInFlightRef.current = false;
        setIsRequestInFlight(false);
      }
    }, [status]);

    const resetState = React.useCallback(() => {
      requestInFlightRef.current = false;
      setMessages([]);
      setInput('');
      setAcceptedLogIds(new Set());
      setIsConfirmCloseOpen(false);
      setIsRequestInFlight(false);
      setPendingComments([]);
      setQueuedRetries([]);
      setRejectedLogIds(new Set());
      pendingTargetsRef.current = undefined;
      resetResolutionLog();
    }, [resetResolutionLog, setMessages]);

    // Self-clean when the workspace closes, even if a caller hides it without
    // calling onClose.
    React.useEffect(() => {
      if (!isOpen && wasOpenRef.current) {
        resetState();
      }
      wasOpenRef.current = isOpen;
    }, [isOpen, resetState]);

    const applyPatch = (patch: FullAssessmentSectionPatch) => {
      switch (patch.kind) {
        case 'capabilities':
          form.setFieldValue('capabilities', patch.value);
          break;
        case 'communities':
          form.setFieldValue('communities', patch.value);
          break;
        case 'killChains':
          form.setFieldValue('killChains', patch.value);
          break;
        case 'services':
          form.setFieldValue('recommendedServices', patch.value);
          break;
        case 'techUses':
          form.setFieldValue('recommendedTechnologyUses', patch.value);
          break;
        case 'tasks':
          form.setFieldValue('taskRecommendations', patch.value);
          break;
        case 'effects':
          form.setFieldValue('effects', patch.value);
          break;
        case 'inters': {
          const others = form.state.values.inters.filter((inter) => inter.type !== patch.type);
          form.setFieldValue('inters', [...others, ...patch.value]);
          break;
        }
        case 'phases':
          form.setFieldValue('data.minPhase', patch.value.minPhase);
          form.setFieldValue('data.maxPhase', patch.value.maxPhase);
          break;
        case 'hurdles':
          form.setFieldValue('data.hurdles', patch.value);
          break;
        case 'expertNote': {
          const notes = form.state.values.data.expertNotes ?? [];
          const hasNote = notes.some((note) => note.identifier === patch.identifier);
          const nextNotes = hasNote
            ? notes.map((note) => (note.identifier === patch.identifier ? { ...note, content: patch.value } : note))
            : [...notes, { content: patch.value, identifier: patch.identifier }];
          form.setFieldValue('data.expertNotes', nextNotes);
          break;
        }
        case 'generalNotes':
          form.setFieldValue('data.generalNotes', patch.value);
          break;
      }
    };

    // Accept replaces that form section wholesale — form state only;
    // nothing persists until Save as Draft / Submit Assessment.
    // See docs/iterations/AZ-393/AZ-393.DECISIONS.md (D6, D7).
    const handleAcceptSection = (sectionId: FullAssessmentSectionId): boolean => {
      const result = sectionStates.results.get(sectionId);
      if (!result || result.status !== FullAssessmentSectionStatus.COMPLETED) return false;
      if (result.logId && rejectedLogIds.has(result.logId)) return false;

      const patch = parseSectionPatch(result);
      if (!patch) {
        errorToast({ description: `The ${FULL_ASSESSMENT_SECTION_LABELS[sectionId]} draft could not be applied.` });
        return false;
      }

      applyPatch(patch);
      recordPatchProvenance(patch, result.logId);

      if (result.logId) {
        const logId = result.logId;
        setAcceptedLogIds((current) => new Set(current).add(logId));
        resolveLog(logId, 'accept');
      }

      return true;
    };

    // Reject discards the section's current draft: the form keeps its previous
    // value and the card falls back to its pre-draft state until a new draft
    // supersedes the rejected one. Returns whether the rejection applied.
    const handleRejectSection = (sectionId: FullAssessmentSectionId): boolean => {
      const result = sectionStates.results.get(sectionId);
      if (!result?.logId || result.status !== FullAssessmentSectionStatus.COMPLETED) return false;

      const logId = result.logId;
      setRejectedLogIds((current) => new Set(current).add(logId));
      resolveLog(logId, 'reject');

      return true;
    };

    // Accept All applies every valid, unresolved draft and skips the rest — a
    // failed or streaming section never blocks it. Everything is in
    // the form afterwards, so the workspace closes itself.
    // See docs/iterations/AZ-393/AZ-393.DECISIONS.md (D7, D11).
    const handleAcceptAll = (): FullAssessmentSectionId[] => {
      const accepted: FullAssessmentSectionId[] = [];
      const acceptedLogIdsAfter = new Set(acceptedLogIds);

      for (const sectionId of orderedSectionIds()) {
        const result = sectionStates.results.get(sectionId);
        if (!isSectionAcceptable(result)) continue;
        if (result?.logId && (acceptedLogIdsAfter.has(result.logId) || rejectedLogIds.has(result.logId))) continue;

        if (handleAcceptSection(sectionId)) {
          accepted.push(sectionId);
          if (result?.logId) {
            acceptedLogIdsAfter.add(result.logId);
          }
        }
      }

      stop();
      rejectUnaccepted({ acceptedLogIds: acceptedLogIdsAfter, results: sectionStates.results });
      resetAndClose();

      if (accepted.length > 0) {
        successToast({
          description: `${accepted.length} section${accepted.length === 1 ? '' : 's'} accepted into the form.`,
        });
      }

      return accepted;
    };

    // Reject All discards every valid, unresolved draft in one pass; the
    // workspace stays open so sections can be regenerated.
    const handleRejectAll = (): FullAssessmentSectionId[] => {
      const rejected: FullAssessmentSectionId[] = [];

      for (const sectionId of orderedSectionIds()) {
        const result = sectionStates.results.get(sectionId);
        if (!isSectionAcceptable(result)) continue;
        if (result?.logId && (acceptedLogIds.has(result.logId) || rejectedLogIds.has(result.logId))) continue;

        if (handleRejectSection(sectionId)) {
          rejected.push(sectionId);
        }
      }

      return rejected;
    };

    const dispatchRetries = React.useCallback(
      (sectionIds: FullAssessmentSectionId[]) => {
        pendingTargetsRef.current = sectionIds;
        const labels = sectionIds.map((sectionId) => FULL_ASSESSMENT_SECTION_LABELS[sectionId]).join(', ');
        dispatchMessage({
          role: 'user',
          parts: [
            {
              type: 'text',
              text: `Regenerate the following section${sectionIds.length === 1 ? '' : 's'}: ${labels}.`,
            },
          ],
        });
      },
      [dispatchMessage],
    );

    // Retry on a finished section stays available while other sections are
    // generating: only one stream runs at a time, so retries clicked mid-stream
    // queue up (toggle to cancel) and dispatch together once the stream ends.
    const handleRetrySection = (sectionId: FullAssessmentSectionId) => {
      if (isBusy || requestInFlightRef.current) {
        setQueuedRetries((current) =>
          current.includes(sectionId) ? current.filter((queued) => queued !== sectionId) : [...current, sectionId],
        );
        return;
      }

      setQueuedRetries((current) => current.filter((queued) => queued !== sectionId));
      dispatchRetries([sectionId]);
    };

    React.useEffect(() => {
      if (isBusy || queuedRetries.length === 0 || requestInFlightRef.current) {
        return;
      }

      const sections = [...queuedRetries];
      setQueuedRetries([]);
      dispatchRetries(sections);
    }, [dispatchRetries, isBusy, queuedRetries]);

    const handleGenerateFullAssessment = () => {
      if (isBusy || requestInFlightRef.current) return;

      dispatchMessage({
        role: 'user',
        parts: [{ type: 'text', text: GENERATE_FULL_ASSESSMENT_MESSAGE }],
      });
    };

    const handleAddComment = (comment: { quote: string; sectionId: FullAssessmentSectionId; text: string }) => {
      setPendingComments((current) => [
        ...current,
        {
          id: generateUUID(),
          quote: `[${FULL_ASSESSMENT_SECTION_LABELS[comment.sectionId]}] ${comment.quote}`,
          sectionId: comment.sectionId,
          text: comment.text,
        },
      ]);
      chatInputRef.current?.focus();
    };

    const handleRemoveComment = (id: string) => {
      setPendingComments((current) => current.filter((comment) => comment.id !== id));
    };

    const handleClearComments = () => {
      setPendingComments([]);
    };

    const resetAndClose = () => {
      resetState();
      onClose();
    };

    // Closing with anything to lose requires confirmation; discarding rejects
    // every unaccepted draft (per-section telemetry). Once every draft is
    // resolved (accepted or rejected) and nothing is in flight, closing is free.
    // See docs/iterations/AZ-393/AZ-393.DECISIONS.md (D7).
    const requestClose = () => {
      const hasUnresolvedDrafts = orderedSectionIds().some((sectionId) => {
        return isUnresolvedDraft({
          acceptedLogIds,
          rejectedLogIds,
          result: sectionStates.results.get(sectionId),
        });
      });

      const isSafeToClose =
        !isBusy && !input.trim() && pendingComments.length === 0 && queuedRetries.length === 0 && !hasUnresolvedDrafts;

      if (isSafeToClose) {
        resetAndClose();
        return;
      }

      setIsConfirmCloseOpen(true);
    };

    const handleDiscardConfirm = () => {
      stop();
      rejectUnaccepted({ acceptedLogIds, results: sectionStates.results });
      resetAndClose();
    };

    const emptyState = (
      <ConversationEmptyState className="text-center">
        <SparklesIcon className="size-6 text-gray-400" />
        <div className="max-w-md space-y-1">
          <h3 className="text-sm font-medium text-gray-800">Full Mission Assessment Generation</h3>
          <div className="text-sm text-gray-500">
            Generate a complete draft of every section in parallel, then refine it in conversation. Your current form
            content is shown on the right and guides the generation.
          </div>
        </div>
        <Button
          onClick={handleGenerateFullAssessment}
          size="sm"
          type="button"
          variant="primary"
        >
          <SparklesIcon size={14} />
          Generate Full Assessment
        </Button>
      </ConversationEmptyState>
    );

    return (
      <>
        <Modal
          centered
          contentClassname="flex min-h-0 overflow-hidden p-3"
          header={{
            className: 'text-gray-800',
            subtitle: (
              <span className="text-gray-700">
                Draft the complete assessment with parallel section generation, then accept the sections you want into
                the form.
              </span>
            ),
            title: 'Generate Full Assessment',
          }}
          height="88vh"
          isOpen={isOpen}
          maxWidth="1500px"
          onClose={requestClose}
          width="95%"
        >
          <Group
            className="h-full min-h-0 w-full"
            defaultLayout={panelLayout.defaultLayout}
            onLayoutChanged={panelLayout.onLayoutChanged}
            orientation="horizontal"
          >
            <Panel
              defaultSize={40}
              id="chat"
              minSize={28}
            >
              <div className="flex h-full min-h-0 flex-col gap-2 pr-1">
                <LlmChatMessages
                  emptyState={emptyState}
                  messages={messages}
                  status={status}
                />

                <LlmChatInput
                  input={input}
                  onClearComments={handleClearComments}
                  onRemoveComment={handleRemoveComment}
                  pendingComments={pendingComments}
                  placeholder="Refine any section, or ask for changes…"
                  sendMessage={sendMessage}
                  setInput={setInput}
                  status={status}
                  stop={stop}
                  textareaRef={chatInputRef}
                />
              </div>
            </Panel>

            <Separator className="group flex w-3 items-center justify-center outline-none">
              <div className="flex h-16 w-1.5 items-center justify-center rounded-full bg-transparent transition-colors group-hover:bg-gray-200">
                <GripVertical className="size-4 text-gray-400" />
              </div>
            </Separator>

            <Panel
              defaultSize={60}
              id="sections"
              minSize={35}
            >
              <div className="h-full min-h-0 pl-1">
                <FullAssessmentSectionsPanel
                  acceptedLogIds={acceptedLogIds}
                  isBusy={isBusy}
                  onAcceptAll={handleAcceptAll}
                  onAcceptSection={handleAcceptSection}
                  onAddComment={handleAddComment}
                  onRejectAll={handleRejectAll}
                  onRejectSection={handleRejectSection}
                  onRetrySection={handleRetrySection}
                  queuedRetrySections={queuedRetries}
                  rejectedLogIds={rejectedLogIds}
                  sectionStates={sectionStates}
                  seedValues={formValues}
                />
              </div>
            </Panel>
          </Group>
        </Modal>

        <ConfirmationModal
          confirmLabel="Discard"
          description="Closing will discard this conversation and every unaccepted section draft. Accepted sections stay in the form. This cannot be undone."
          isOpen={isConfirmCloseOpen}
          onConfirm={handleDiscardConfirm}
          setIsOpen={setIsConfirmCloseOpen}
          title="Discard this generation?"
          variant="destructive"
        />
      </>
    );
  },
});
